import {
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    EnvelopeIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import { ago, SentMessageType } from "@sp-crm/core";
import React from "react";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    ReceivedStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";
import { UnifiedMessage } from "./unified-message";

interface MessageStatusProps {
    message: UnifiedMessage;
}

const MessageStatus: React.FC<MessageStatusProps> = ({ message }) => {
    if (message.type === "received") {
        return <ReceivedStatusMessage mode="icon" />;
    }

    if (message.type === "sms") {
        return null;
    }

    const { status, latestEventStatus, messageType } = message;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

const messageTypeLabels: Record<SentMessageType, string> = {
    ClientIntake: "Client Referral",
    ClientIntakeFax: "Client Referral (fax)",
    CommunityComparison: "Community Comparison",
    Email: "",
    Invoice: "Invoice",
    DisclosureRequest: "Disclosure Request",
    DisclosureReceipt: "Disclosure Receipt",
    HostedFormRequest: "Share Input Form",
    TestOnlyMessage: "",
    SignatureCompletion: "Signature Completion",
    SignatureRequest: "Signature Request",
    BulkMessage: "Outreach",
    HostedFormReferralLink: "Share Input Form w/ Referral",
    AvailabilityRequest: "Availability Request",
    AvailabilityUpdateRequest: "Availability Update Request",
    PricingUpdateRequest: "Pricing Update Request",
    PricingRequest: "Pricing Request",
};

interface MessageRowProps {
    message: UnifiedMessage;
    isExpanded: boolean;
    onToggleExpand: () => void;
    children?: React.ReactNode;
}

export const MessageRow: React.FC<MessageRowProps> = ({
    message,
    isExpanded,
    onToggleExpand,
    children,
}) => {
    const messageTypeLabel =
        message.messageType && messageTypeLabels[message.messageType]
            ? messageTypeLabels[message.messageType]
            : "";

    const Icon = message.type === "sms" ? ChatBubbleLeftRightIcon : EnvelopeIcon;

    const participantLine =
        message.type === "received"
            ? `From: ${message.fromDisplay}`
            : `To: ${message.toDisplay}`;

    return (
        <li className="mb-2">
            <div
                className={`bg-white rounded-lg shadow px-4 py-3 cursor-pointer transition-colors ${
                    isExpanded ? "" : "hover:bg-brand-50"
                }`}
                onClick={onToggleExpand}>
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900 truncate">
                                        {message.subject}
                                    </span>
                                    {messageTypeLabel ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-500 flex-shrink-0">
                                            {messageTypeLabel}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-sm text-gray-500 truncate mt-0.5">
                                    {participantLine}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <MessageStatus message={message} />
                                {message.attachmentCount > 0 ? (
                                    <span className="inline-flex items-center gap-0.5 text-gray-400">
                                        <PaperClipIcon className="h-4 w-4" />
                                        <span className="text-xs">
                                            {message.attachmentCount}
                                        </span>
                                    </span>
                                ) : null}
                                <span className="text-sm text-gray-400 whitespace-nowrap">
                                    {ago(message.timestamp, "")}
                                </span>
                                {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {isExpanded && children ? (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                {children}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </li>
    );
};
